import React, { useEffect, useState } from 'react';
import Loading from '../../../components/common/Loading';
import { useTranslation } from 'react-i18next';
import { Request } from '../../../config/requests';
import { getToken } from '../../../config/token';
import { Box, Skeleton } from '@mui/material';
import Header from '../../../components/common/Header';
import Footer from '../../../components/common/Footer';
import { isMobile } from 'react-device-detect';
import PawUI from '../../../components/ui/PawUI';
import PaymentDashboard from './PaymentDashboard';
import ModalBulkCharge from './ModalBulkCharge';
import { showToast } from '../../../config/helpers';
import ModalExportClaimTasks from './ModalExportClaimTasks';
import { useLocation, useNavigate } from 'react-router-dom';

function PaymentPortal() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { search } = useLocation();
    const token = getToken();
    const [isLoadingPolicies, setIsLoadingPolicies] = useState(false);
    const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
    const [isAllocating, setIsAllocating] = useState(false);
    const [policiesDetails, setPoliciesDetails] = useState([]);
    const [invoicesDetails, setInvoicesDetails] = useState([]);
    const [dashboardMode, setDashboardMode] = useState(null); // null, 'policies', 'invoices'
    const [currentPolicyFilter, setCurrentPolicyFilter] = useState(null); // Track current policy id filter
    const [currentPolicySearch, setCurrentPolicySearch] = useState(null); // Track current policy search term
    const [bulkChargeOpen, setBulkChargeOpen] = useState(false);
    const [bulkChargeInvoices, setBulkChargeInvoices] = useState([]);
    const [currentPolicySubscriptionId, setCurrentPolicySubscriptionId] = useState(null);
    const [currentPolicyEmail, setCurrentPolicyEmail] = useState(null);
    const [openExportTasksModal, setOpenExportTasksModal] = useState(false);
    const [isExportTasksLoading, setIsExportTasksLoading] = useState(false);
    const [exportTasksFileUrl, setExportTasksFileUrl] = useState(null);
    const [exportTasksFileName, setExportTasksFileName] = useState('claim_tasks.xlsx');
    const [exportTasksError, setExportTasksError] = useState('');

    // AbortController refs for cancelling requests
    const invoicesAbortController = React.useRef(null);
    const policiesAbortController = React.useRef(null);

    // Dashboard pagination states
    const [dashboardCurrentPage, setDashboardCurrentPage] = useState(1);
    const [dashboardLoadedPages, setDashboardLoadedPages] = useState(new Map());

    const pageSize = 50;

    const extractFilename = (contentDisposition) => {
        if (!contentDisposition) return 'claim_tasks.xlsx';
        const utfMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (utfMatch?.[1]) return decodeURIComponent(utfMatch[1]);
        const asciiMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
        if (asciiMatch?.[1]) return asciiMatch[1];
        return 'claim_tasks.xlsx';
    };

    const cleanupExportTasksUrl = () => {
        if (exportTasksFileUrl) {
            URL.revokeObjectURL(exportTasksFileUrl);
            setExportTasksFileUrl(null);
        }
    };

    const handleOpenExportTasksModal = () => {
        cleanupExportTasksUrl();
        setExportTasksError('');
        setExportTasksFileName('claim_tasks.xlsx');
        setOpenExportTasksModal(true);
    };

    const handleCloseExportTasksModal = (isOpen) => {
        if (isOpen === false) {
            setOpenExportTasksModal(false);
            cleanupExportTasksUrl();
            setExportTasksError('');
            return;
        }
        setOpenExportTasksModal(Boolean(isOpen));
    };

    const handleExportClaimTasks = async () => {
        setIsExportTasksLoading(true);
        setExportTasksError('');
        cleanupExportTasksUrl();

        try {
            const response = await Request({
                method: 'get',
                route: 'api/report/export-tasks',
                token,
            });

            const blob = await response.blob();
            const fileName = extractFilename(response.headers.get('content-disposition'));
            const fileUrl = URL.createObjectURL(blob);
            setExportTasksFileName(fileName);
            setExportTasksFileUrl(fileUrl);
        } catch (err) {
            console.error('Error exporting claim tasks:', err);
            setExportTasksError(t('somethingWentWrong'));
        } finally {
            setIsExportTasksLoading(false);
        }
    };

    const handleDownloadClaimTasks = () => {
        if (!exportTasksFileUrl) return;
        const link = document.createElement('a');
        link.href = exportTasksFileUrl;
        link.download = exportTasksFileName || 'claim_tasks.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const allocatePayments = async () => {
        const selectedInvoices = invoicesDetails.filter(invoice => invoice.selected);
        if (selectedInvoices.length === 0) {
            showToast(t('noInvoicesSelected'), { error: true });
            return;
        }

        setIsAllocating(true);

        // Example payload for testing

        // const payload = [
        //     {
        //         "policyId": "2786",
        //         "policyNo": "P80004624",
        //         "invoiceNo": 100093805,
        //         "amount": 75.57,
        //         "paymentDate": "2025-10-08",
        //         "dueDate": "2025-07-04",
        //         "skipPaymentCreation": false,
        //     },
        // ]

        const payload = selectedInvoices
            .filter(invoice => !(invoice.paid === true && invoice.payment_status === "PAID"))
            .map(invoice => ({
                policyId: String(invoice.policy_id),
                policyNo: invoice.policy_no,
                invoiceNo: Number(invoice.invoice_number),
                amount: parseFloat(invoice.payment_amount),
                paymentDate: invoice.payment_date,
                dueDate: invoice.due_date,
                skipPaymentCreation: invoice.paid === false && invoice.payment_status === "PAID",
            }));

        Request({
            method: 'post',
            route: 'api/payment/allocate',
            token,
            values: payload,
        })
            .then((response) => {
                if (response && response.ok) {
                    return response.json();
                }
            })
            .then((result) => {
                if (!result) return;

                const { results } = result;
                const allSuccessful = results.every(r => r.success);

                if (allSuccessful) {
                    showToast(t('allocationSuccessful'), { error: false });

                    // Refresh invoices after successful allocation
                    setTimeout(() => {
                        if (currentPolicyFilter) {
                            getInvoicesReport(currentPolicyFilter, true);
                        }
                    }, 1000);
                } else {
                    // Handle partial success or failures
                    const failedCount = results.filter(r => !r.success).length;
                    const successCount = results.filter(r => r.success).length;

                    if (successCount > 0) {
                        showToast(t('partialAllocationSuccess', { success: successCount, failed: failedCount }), { error: false });

                        // Refresh invoices after partial success
                        setTimeout(() => {
                            if (currentPolicyFilter) {
                                getInvoicesReport(currentPolicyFilter, true);
                            }
                        }, 1000);
                    } else {
                        showToast(t('allocationFailed'), { error: true });
                    }
                }
            })
            .catch((error) => {
                // Error handling is done in Request function, just log here
                console.error('Error allocating payments:', error);
            })
            .finally(() => {
                setIsAllocating(false);
            });
    };

    const chargePayment = async (invoice, autoAdvance) => {
        const payload = {
            policyId: Number(invoice.policy_id),
            invoiceNo: String(invoice.invoice_number),
            amount: parseFloat(invoice.payment_amount),
            email: invoice.email || '',
            policyNumber: invoice.policy_no,
            stripeSubscription: invoice.subscription_id || '',
            autoAdvance,
        };

        const response = await Request({
            method: 'post',
            route: 'api/payment/charge',
            token,
            values: payload,
        });

        const result = await response.json();

        if (!response.ok) {
            throw result;
        }

        return result;
    };

    /**
     * Open the bulk-charge dialog for whatever is ticked.
     *
     * No payment-date requirement here — that belongs to allocation. The only thing
     * refused up front is an empty selection; every other check is the server's, because
     * only Stripe knows whether an instalment has already been paid.
     */
    const chargeSelected = () => {
        const selected = (invoicesDetails || []).filter((invoice) => invoice.selected);
        if (selected.length === 0) {
            showToast(t('noInvoicesSelected'), { error: true });
            return;
        }
        setBulkChargeInvoices(selected);
        setBulkChargeOpen(true);
    };

    const handleChargeComplete = () => {
        if (currentPolicyFilter) {
            getInvoicesReport(currentPolicyFilter, true);
        }
    };

    const getDashboardData = (currentPage = 1, mode = dashboardMode, policyId = null) => {
        if (mode === 'policies') {
            getPolicieslReport(currentPage, false, currentPolicySearch);
        } else if (mode === 'invoices') {
            getInvoicesReport(policyId);
        }
    };

    const handlePolicySearch = (searchTerm) => {
        setCurrentPolicySearch(searchTerm);
        setDashboardCurrentPage(1);
        setDashboardLoadedPages(new Map()); // Clear cache when searching
        getPolicieslReport(1, true, searchTerm);
    };

    const getPolicieslReport = (currentPage = 1, forceRefetch = false, policySearch = null) => {
        // Cancel any ongoing invoices request
        if (invoicesAbortController.current) {
            invoicesAbortController.current.abort();
            setIsLoadingInvoices(false);
        }

        setIsLoadingPolicies(true);

        // Create cache key that includes search term
        const cacheKey = policySearch ? `search-${policySearch}-${currentPage}` : currentPage;

        if (dashboardLoadedPages.has(cacheKey) && !forceRefetch) {
            const { policies } = dashboardLoadedPages.get(cacheKey);
            setPoliciesDetails(policies);
            setIsLoadingPolicies(false);
            return;
        }

        // Create new AbortController for this request
        policiesAbortController.current = new AbortController();

        let queryString = `?page=${currentPage}&pageSize=${pageSize}`;
        if (policySearch) {
            queryString += `&policyNo=${encodeURIComponent(policySearch)}`;
        }

        Request({
            method: 'get',
            route: `api/payment/policies${queryString}`,
            token,
            signal: policiesAbortController.current.signal,
        })
            .then((response) => {
                if (!response.ok) {
                    showToast(t('somethingWentWrong'), { error: true });
                    throw new Error(`Request failed with status ${response.status}`);
                }
                return response.json();
            })
            .then((json) => {
                // Transform policies data for the table
                const formattedPolicies = json.policies.map((policy) => ({
                    id: policy.id,
                    policy_id: policy.policy_id,
                    policy_number: policy.policy_number,
                    customer_name: policy.customer_name,
                    status: policy.status,
                    date_issue: policy.date_issue,
                    subscription_id: policy.subscription_id,
                    email: policy.email,
                    selected: false,
                }));

                // Store pagination data with correct property names using the cache key
                setDashboardLoadedPages((prev) => new Map(prev).set(cacheKey, {
                    policies: formattedPolicies,
                    startIndex: json.startIndex,
                    endIndex: json.endIndex,
                    totalPolicies: json.total,
                    hasNextPage: json.hasNextPage,
                    searchTerm: policySearch,
                }));

                setPoliciesDetails(formattedPolicies);
                setDashboardMode('policies');
            })
            .catch((err) => {
                if (err.name === 'AbortError') {
                    console.log('Policies request was aborted');
                    return;
                }
                console.error('Error getPolicieslReport:', err);
                showToast(t('somethingWentWrong'), { error: true });
            })
            .finally(() => {
                setIsLoadingPolicies(false);
                policiesAbortController.current = null;
            });
    };

    const getInvoicesReport = (policyId = null, forceRefetch = false, policySubscriptionId = currentPolicySubscriptionId, policyEmail = currentPolicyEmail) => {
        // Cancel any ongoing policies request
        if (policiesAbortController.current) {
            policiesAbortController.current.abort();
            setIsLoadingPolicies(false);
        }

        if (!policyId) {
            showToast(t('policyNumberRequired'), { error: true });
            return;
        }

        setIsLoadingInvoices(true);

        // LIVE FROM INSLY when the policy was found by an exact policy-number search.
        //
        // That search is the first step of charging or allocating, and the replica lags
        // behind every allocation, MTA and reactivation by minutes — long enough for an
        // admin to act on a credit note that is not there yet, or on an instalment that
        // has just been paid. Browsing the list stays on the replica: one query for the
        // whole grid, and nobody charges anything from a page they are scrolling.
        const useInsly = Boolean(currentPolicySearch);

        // Cache by policyId AND source: the two can legitimately disagree for a few
        // minutes, and a cached replica row must not be served as the live one.
        const cacheKey = `invoices-${policyId}-${useInsly ? 'insly' : 'replica'}`;
        if (dashboardLoadedPages.has(cacheKey) && !forceRefetch) {
            const { invoices } = dashboardLoadedPages.get(cacheKey);
            setInvoicesDetails(invoices);
            setIsLoadingInvoices(false);
            return;
        }

        // Create new AbortController for this request
        invoicesAbortController.current = new AbortController();

        const queryString = `?policyId=${encodeURIComponent(policyId)}`
            + (useInsly ? '&source=insly' : '');

        Request({
            method: 'get',
            route: `api/payment/invoices${queryString}`,
            token,
            signal: invoicesAbortController.current.signal,
        })
            .then((response) => {
                if (!response.ok) {
                    showToast(t('somethingWentWrong'), { error: true });
                    throw new Error(`Request failed with status ${response.status}`);
                }
                return response.json();
            })
            .then((invoicesArray) => {
                const formattedInvoices = invoicesArray.map((invoice) => ({
                    invoice_number: invoice.invoiceNo,
                    policy_id: invoice.policyId,
                    policy_no: invoice.policyNo,
                    payment_period: invoice.period,
                    payment_amount: invoice.amount,
                    payment_status: invoice.status,
                    payment_date: invoice.paymentDate,
                    stripe_id: invoice.stripeId,
                    payment_source: invoice.paymentSource,
                    payment_method: invoice.paymentMethod,
                    payment_method_details: invoice.paymentMethodDetails,
                    due_date: invoice.dueDate,
                    paid: invoice.paid,
                    subscription_id: policySubscriptionId,
                    email: policyEmail,
                    selected: false,
                    had_no_payment_date: invoice.paymentDate === null,
                }));

                // Store invoices data (no pagination metadata)
                setDashboardLoadedPages((prev) => new Map(prev).set(cacheKey, {
                    invoices: formattedInvoices,
                }));

                setInvoicesDetails(formattedInvoices);
                setDashboardMode('invoices');
            })
            .catch((err) => {
                if (err.name === 'AbortError') {
                    console.log('Invoices request was aborted');
                    return;
                }
                console.error('Error getInvoicesReport:', err);
                showToast(t('somethingWentWrong'), { error: true });
            })
            .finally(() => {
                setIsLoadingInvoices(false);
                invoicesAbortController.current = null;
            });
    };

    const handlePaymentDateUpdate = (formattedDate, invoiceData) => {
        // Update the invoice in the state with the new payment date
        setInvoicesDetails((prevInvoices) => {
            return prevInvoices.map((invoice) => {
                if (invoice.invoice_number === invoiceData.invoice_number) {
                    return {
                        ...invoice,
                        payment_date: formattedDate,
                    };
                }
                return invoice;
            });
        });

        // Also update the cache
        const cacheKey = `invoices-${invoiceData.policy_no}`;
        if (dashboardLoadedPages.has(cacheKey)) {
            const cachedData = dashboardLoadedPages.get(cacheKey);
            const updatedInvoices = cachedData.invoices.map((invoice) => {
                if (invoice.invoice_number === invoiceData.invoice_number) {
                    return {
                        ...invoice,
                        payment_date: formattedDate,
                    };
                }
                return invoice;
            });
            setDashboardLoadedPages((prev) => new Map(prev).set(cacheKey, {
                invoices: updatedInvoices,
            }));
        }
    };

    useEffect(() => {
        // Fetch first page of policies on component mount
        getPolicieslReport(1, true, null);

        // Cleanup function to abort any ongoing requests
        return () => {
            if (invoicesAbortController.current) {
                invoicesAbortController.current.abort();
            }
            if (policiesAbortController.current) {
                policiesAbortController.current.abort();
            }
        };
    }, []);

    useEffect(() => {
        return () => {
            if (exportTasksFileUrl) {
                URL.revokeObjectURL(exportTasksFileUrl);
            }
        };
    }, [exportTasksFileUrl]);

    useEffect(() => {
        const params = new URLSearchParams(search);
        if (params.get('exportTasks') === '1') {
            handleOpenExportTasksModal();
            params.delete('exportTasks');
            const nextSearch = params.toString();
            navigate(
                {
                    pathname: '/payment-portal',
                    search: nextSearch ? `?${nextSearch}` : '',
                },
                { replace: true }
            );
        }
    }, [search, navigate]);

    return (
        <div className="main-container SlowShow">
            <Header />
            <div className="page-content relative claims-content">
                <div className="claims" style={{maxWidth: 'unset'}}>
                    {isMobile ? (
                        <>
                            <PawUI
                                top="1vh"
                                right="6vw"
                                rotation="right"
                                width="25vw"
                                zIndex="0"
                            />
                            <PawUI
                                top="53vh"
                                left="-20vw"
                                rotation="right"
                                width="60vw"
                                zIndex="0"
                            />
                            <PawUI
                                bottom="-23vh"
                                right="-27vw"
                                rotation="left"
                                width="70vw"
                                zIndex="0"
                            />
                        </>
                    ) : (
                        <>
                            <PawUI
                                top="20vh"
                                right="1vw"
                                rotation="right"
                                width="350px"
                                zIndex="0"
                            />
                            <PawUI
                                bottom="-10vh"
                                left="5vw"
                                rotation="left"
                                width="150px"
                                zIndex="0"
                            />
                            <PawUI
                                top="10vh"
                                left="20vw"
                                rotation="left"
                                width="250px"
                                zIndex="0"
                            />
                        </>
                    )}
                    <div className="w-100 my-3 d-flex justify-content-between align-items-center dark_red_title relative">
                        <div style={{ whiteSpace: 'nowrap' }}>{t('paymentPortal')}</div>
                        <Box
                            sx={{
                                display: 'flex',
                                columnGap: '20px',
                            }}
                        >
                            {dashboardMode === 'invoices' &&
                                <button
                                    className='button_red'
                                    style={{
                                        width: '220px',
                                        margin: '0',
                                    }}
                                    disabled={isLoadingPolicies || isAllocating}
                                    onClick={() => {
                                        setDashboardCurrentPage(1);
                                        setDashboardLoadedPages(new Map());
                                        setCurrentPolicyFilter(null);
                                        setCurrentPolicySearch(null);
                                        getPolicieslReport(1, true, null);
                                    }}
                                >
                                    {isLoadingPolicies || isAllocating ? (
                                        <Loading noText fast scroll={false} color={'#fff'} />
                                    ) : (
                                        t('backToPolicies')
                                    )}
                                </button>}
                        </Box>
                    </div>

                    {isLoadingPolicies || isLoadingInvoices || isAllocating ? (
                        <Skeleton
                            variant="rounded"
                            width={'100%'}
                            height={550}
                            style={{ margin: '2vh 0' }}
                        />
                    ) : (
                        <PaymentDashboard
                            tableDetails={dashboardMode === 'policies' ? policiesDetails : invoicesDetails}
                            setTableDetails={dashboardMode === 'policies' ? setPoliciesDetails : setInvoicesDetails}
                            dashboardMode={dashboardMode}
                            currentPage={dashboardCurrentPage}
                            setCurrentPage={setDashboardCurrentPage}
                            loadedPages={dashboardLoadedPages}
                            getDashboardData={getDashboardData}
                            onCheckInvoices={(policyId) => {
                                setDashboardCurrentPage(1);
                                setDashboardLoadedPages(new Map());
                                setCurrentPolicyFilter(policyId);
                                const policy = policiesDetails.find(p => p.policy_id === policyId);
                                const subId = policy?.subscription_id || null;
                                const email = policy?.email || null;
                                setCurrentPolicySubscriptionId(subId);
                                setCurrentPolicyEmail(email);
                                getInvoicesReport(policyId, true, subId, email);
                            }}
                            currentPolicyFilter={currentPolicyFilter}
                            onAllocate={allocatePayments}
                            onCharge={chargePayment}
                            onChargeSelected={chargeSelected}
                            onChargeComplete={handleChargeComplete}
                            onPolicySearch={handlePolicySearch}
                            currentPolicySearch={currentPolicySearch}
                            onPaymentDateUpdate={handlePaymentDateUpdate}
                        />
                    )}
                </div>
            </div>
            <ModalBulkCharge
                openModal={bulkChargeOpen}
                setOpenModal={setBulkChargeOpen}
                invoices={bulkChargeInvoices}
                // The whole grid, so an invoice can be ADDED to the batch by number and
                // still carry its real due date and amount.
                allInvoices={invoicesDetails || []}
                token={token}
                policy={{
                    // One policy per grid, so the first selected row carries the context
                    // the whole batch is charged against.
                    policyId: bulkChargeInvoices[0]?.policy_id,
                    policyNumber: bulkChargeInvoices[0]?.policy_no,
                    stripeSubscription: bulkChargeInvoices[0]?.subscription_id || currentPolicySubscriptionId || '',
                    email: bulkChargeInvoices[0]?.email || currentPolicyEmail || '',
                }}
                onComplete={handleChargeComplete}
            />
            <ModalExportClaimTasks
                openModal={openExportTasksModal}
                setOpenModal={handleCloseExportTasksModal}
                onConfirmExport={handleExportClaimTasks}
                onDownload={handleDownloadClaimTasks}
                isLoading={isExportTasksLoading}
                downloadReady={Boolean(exportTasksFileUrl)}
                errorMessage={exportTasksError}
            />
            <Footer />
        </div>
    );
}

export default PaymentPortal;
